/**
 * Recurring Tickets — Jotai atoms (feature-scoped).
 *
 * Scope: UI state that is ONLY meaningful inside the recurring-tickets screen
 * (drawer visibility, selection, dialog toggles, currently-viewed mapping id).
 *
 * Selection is intentionally array-typed so multi-select can be enabled in a
 * future release by removing the single-select guard in `recurringTicketSelectionSingleAtom`.
 * See GATE2_DECISIONS.md §4.
 *
 * URL-based filter state does NOT live here — see `useRecurringTicketUrlFilters`
 * which treats `useSearchParams` as the source of truth.
 */

import { atom } from "jotai";

// ----------------------------------------------------------------------------
// Drawer visibility
// ----------------------------------------------------------------------------

export const isRecurringTicketFilterDrawerOpenAtom = atom<boolean>(false);

/**
 * Which tab the filter drawer should open to when triggered from a chip click.
 * Defaults to "Customers" (per PRD order + design default).
 */
export const recurringTicketFilterDrawerTabAtom = atom<string>("Customers");

// ----------------------------------------------------------------------------
// Dialog toggles (pause / delete confirmation)
// ----------------------------------------------------------------------------

export const isPauseDialogOpenAtom = atom<boolean>(false);
export const isDeleteDialogOpenAtom = atom<boolean>(false);

// ----------------------------------------------------------------------------
// Active / currently-viewed mapping id
// ----------------------------------------------------------------------------

export const activeRecurringTicketIdAtom = atom<string | null>(null);

/**
 * Transient "action-in-flight" mapping id. Set by row / bulk action handlers
 * just before opening a confirmation dialog (pause / delete) or routing to
 * the duplicate / create-now flows. The dialog reads this atom to know which
 * ticket to operate on without threading the id through React context.
 */
export const pendingActionRecurringTicketIdAtom = atom<string | null>(null);

// ----------------------------------------------------------------------------
// Selection (array-typed for future multi-select)
// ----------------------------------------------------------------------------

/**
 * Base selection atom. Array-typed for forward compatibility with multi-select.
 * DO NOT write to this atom directly from components — go through
 * `recurringTicketSelectionSingleAtom` which enforces the v1 single-select guard.
 */
export const recurringTicketSelectionAtom = atom<string[]>([]);

/**
 * Derived single-select setter. Enforces length ≤ 1 by overwriting any previous
 * selection when a new id is set. Passing `null` clears the selection.
 *
 * When the feature upgrades to multi-select, remove this atom and let components
 * write to `recurringTicketSelectionAtom` directly.
 */
export const recurringTicketSelectionSingleAtom = atom<
  string | null,
  [string | null],
  void
>(
  (get) => get(recurringTicketSelectionAtom)[0] ?? null,
  (_get, set, id) => {
    set(recurringTicketSelectionAtom, id ? [id] : []);
  }
);
